import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Lightbulb, ThumbsUp, MessageCircle, Plus, Send, 
  Loader2, CheckCircle2, Clock, XCircle, Eye,
  Flame, Calendar, Trash2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MentionInput } from '@/components/shared/MentionInput';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryColors = {
  channel: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  company: { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  process: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  general: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  other: { bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' }
};

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Eye },
  reviewing: { label: 'Reviewing', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
  implemented: { label: 'Implemented', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle }
};

export default function IdeasBoardPage({ embedded = false }) {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [newIdea, setNewIdea] = useState({ title: '', description: '', category: 'general' });
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentUserId = currentUser.id;

  useEffect(() => { fetchIdeas(); }, [filter]);
  const fetchIdeas = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = filter === 'all' ? `${API}/ideas` : `${API}/ideas?status=${filter}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setIdeas(res.data.ideas);
    } catch { toast.error('Failed to load ideas'); }
    finally { setLoading(false); }
  };

  const handleAddIdea = async () => {
    if (!newIdea.title.trim() || !newIdea.description.trim()) { toast.error('Please fill in all fields'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ideas`, newIdea, { headers: { Authorization: `Bearer ${token}` } });
      setIdeas([res.data.idea, ...ideas]);
      setShowAddModal(false);
      setNewIdea({ title: '', description: '', category: 'general' });
      toast.success('Idea submitted!');
    } catch { toast.error('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleUpvote = async (ideaId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ideas/${ideaId}/upvote`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setIdeas(ideas.map(i => {
        if (i.id === ideaId) {
          const upvotes = [...(i.upvotes || [])];
          if (res.data.action === 'added') upvotes.push(currentUserId);
          else { const idx = upvotes.indexOf(currentUserId); if (idx > -1) upvotes.splice(idx, 1); }
          return { ...i, upvotes };
        }
        return i;
      }));
      if (selectedIdea?.id === ideaId) {
        setSelectedIdea(prev => {
          const upvotes = [...(prev.upvotes || [])];
          if (res.data.action === 'added') upvotes.push(currentUserId);
          else { const idx = upvotes.indexOf(currentUserId); if (idx > -1) upvotes.splice(idx, 1); }
          return { ...prev, upvotes };
        });
      }
    } catch { toast.error('Failed to upvote'); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedIdea) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/ideas/${selectedIdea.id}/comment`, { content: newComment }, { headers: { Authorization: `Bearer ${token}` } });
      const updated = { ...selectedIdea, comments: [...(selectedIdea.comments || []), res.data.comment] };
      setIdeas(ideas.map(i => i.id === selectedIdea.id ? updated : i));
      setSelectedIdea(updated);
      setNewComment('');
    } catch { toast.error('Failed to add comment'); }
  };

  const handleDeleteIdea = async (ideaId) => {
    if (!window.confirm('Delete this idea?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/ideas/${ideaId}`, { headers: { Authorization: `Bearer ${token}` } });
      setIdeas(ideas.filter(i => i.id !== ideaId));
      setSelectedIdea(null);
      toast.success('Idea deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const canEditIdea = (idea) => {
    try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return idea.user_id === u.id || ['admin', 'developer', 'manager'].includes(u.role_id); } catch { return false; }
  };

  const sortedIdeas = [...ideas].sort((a, b) => sortBy === 'popular' ? (b.upvotes || []).length - (a.upvotes || []).length : 0);

  const stats = {
    total: ideas.length,
    open: ideas.filter(i => i.status === 'open').length,
    approved: ideas.filter(i => i.status === 'approved' || i.status === 'implemented').length,
    totalVotes: ideas.reduce((sum, i) => sum + (i.upvotes || []).length, 0)
  };

  if (loading) {
    if (embedded) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div>;
    return <ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>;
  }

  const content = (
    <>
      {!embedded && (
        <SectionHeader
          icon={Lightbulb}
          title="Ideas Board"
          subtitle="Share ideas and suggestions for the team"
          iconBg="bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/20"
          action={<Button data-testid="add-idea-btn" onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/20"><Plus size={18} className="mr-2" /> Share Idea</Button>}
        />
      )}
      {embedded && (
        <div className="flex justify-end mb-4">
          <Button data-testid="add-idea-btn" onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/20"><Plus size={18} className="mr-2" /> Share Idea</Button>
        </div>
      )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Ideas', value: stats.total, accent: 'border-[hsl(var(--border))]', vc: '' },
            { label: 'Open', value: stats.open, accent: 'border-blue-500/30', vc: 'text-blue-400' },
            { label: 'Approved', value: stats.approved, accent: 'border-emerald-500/30', vc: 'text-emerald-400' },
            { label: 'Total Votes', value: stats.totalVotes, accent: 'border-red-500/30', vc: 'text-red-400' },
          ].map((s) => (
            <div key={s.label} className={`bg-[hsl(var(--card))] border ${s.accent} rounded-xl p-4`}>
              <p className={`text-xs ${s.vc || 'text-[hsl(var(--text-muted))]'} mb-1`}>{s.label}</p>
              <p className={`text-2xl font-bold ${s.vc}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filter + Sort */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {['all', 'open', 'reviewing', 'approved', 'implemented'].map((f) => (
              <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} className={filter === f ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0' : 'border-[hsl(var(--border))] text-[hsl(var(--text-secondary))]'}>
                {f === 'all' ? 'All' : statusConfig[f]?.label || f}
              </Button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => setSortBy(sortBy === 'newest' ? 'popular' : 'newest')} className="border-[hsl(var(--border))] text-[hsl(var(--text-secondary))]">
            {sortBy === 'popular' ? <><Flame size={14} className="mr-1" /> Popular</> : <><Clock size={14} className="mr-1" /> Latest</>}
          </Button>
        </div>

        {/* Ideas Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedIdeas.length === 0 ? (
            <div className="col-span-full text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
              <Lightbulb size={48} className="mx-auto text-amber-500/30 mb-4" />
              <p className="text-[hsl(var(--text-secondary))] font-medium">No ideas found</p>
            </div>
          ) : sortedIdeas.map((idea) => {
            const status = statusConfig[idea.status] || statusConfig.open;
            const StatusIcon = status.icon;
            const hasUpvoted = (idea.upvotes || []).includes(currentUserId);
            const cat = categoryColors[idea.category] || categoryColors.general;
            return (
              <div key={idea.id} data-testid={`idea-${idea.id}`} className="group bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-amber-500/30 transition-all cursor-pointer" onClick={() => setSelectedIdea(idea)}>
                <div className="flex items-start justify-between mb-3">
                  <Badge className={`${cat.bg} ${cat.text} border ${cat.border} text-xs`}>{idea.category}</Badge>
                  <Badge className={`${status.color} border text-xs`}><StatusIcon size={10} className="mr-1" />{status.label}</Badge>
                </div>
                <h3 className="font-semibold mb-2 line-clamp-2 group-hover:text-amber-400 transition-colors">{idea.title}</h3>
                <p className="text-sm text-[hsl(var(--text-muted))] line-clamp-2 mb-4">{idea.description}</p>
                <div className="flex items-center justify-between text-sm border-t border-[hsl(var(--border))] pt-3">
                  <div className="flex items-center gap-4">
                    <button onClick={(e) => { e.stopPropagation(); handleUpvote(idea.id); }} className={`flex items-center gap-1.5 font-medium transition-colors ${hasUpvoted ? 'text-amber-400' : 'text-[hsl(var(--text-muted))] hover:text-amber-400'}`}>
                      <ThumbsUp size={14} fill={hasUpvoted ? 'currentColor' : 'none'} /> {(idea.upvotes || []).length}
                    </button>
                    <span className="flex items-center gap-1.5 text-[hsl(var(--text-muted))]"><MessageCircle size={14} />{(idea.comments || []).length}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {idea.user_avatar ? (
                      <img src={idea.user_avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-[8px] font-bold">{idea.user_name?.charAt(0)}</div>
                    )}
                    <span className="text-xs text-[hsl(var(--text-muted))]">{idea.user_name}</span>
                  </div>
                  {canEditIdea(idea) && <button onClick={(e) => { e.stopPropagation(); handleDeleteIdea(idea.id); }} className="text-red-400/60 hover:text-red-400 transition-colors ml-1" data-testid={`delete-idea-${idea.id}`}><Trash2 size={13} /></button>}
                </div>
              </div>
            );
          })}
        </div>

        {/* Add Idea Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Lightbulb size={18} className="text-amber-400" /> Share New Idea</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div><label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Title *</label><Input value={newIdea.title} onChange={e => setNewIdea({...newIdea, title: e.target.value})} placeholder="Your idea title" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" /></div>
              <div><label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description *</label><textarea value={newIdea.description} onChange={e => setNewIdea({...newIdea, description: e.target.value})} placeholder="Describe your idea..." rows={4} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500/30" /></div>
              <div><label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Category</label>
                <Select value={newIdea.category} onValueChange={v => setNewIdea({...newIdea, category: v})}><SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger><SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]"><SelectItem value="channel">Channel</SelectItem><SelectItem value="company">Company</SelectItem><SelectItem value="process">Process</SelectItem><SelectItem value="general">General</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button><Button onClick={handleAddIdea} disabled={submitting} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white">{submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Idea Detail - Social Media Style Comments */}
        <Dialog open={!!selectedIdea} onOpenChange={() => setSelectedIdea(null)}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-lg max-h-[80vh] flex flex-col p-0 gap-0">
            {selectedIdea && (
              <>
                {/* Post Header */}
                <div className="p-5 border-b border-[hsl(var(--border))]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm">{selectedIdea.user_name?.charAt(0)}</div>
                    <div>
                      <p className="font-semibold text-sm">{selectedIdea.user_name}</p>
                      <p className="text-[10px] text-[hsl(var(--text-muted))] flex items-center gap-1"><Calendar size={9} /> {new Date(selectedIdea.created_at).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                    </div>
                    <div className="ml-auto flex gap-1.5">
                      <Badge className={`${(categoryColors[selectedIdea.category] || categoryColors.general).bg} ${(categoryColors[selectedIdea.category] || categoryColors.general).text} border ${(categoryColors[selectedIdea.category] || categoryColors.general).border} text-[10px]`}>{selectedIdea.category}</Badge>
                      <Badge className={`${statusConfig[selectedIdea.status]?.color} border text-[10px]`}>{statusConfig[selectedIdea.status]?.label}</Badge>
                    </div>
                  </div>
                  <h3 className="font-bold text-lg mb-2">{selectedIdea.title}</h3>
                  <p className="text-sm text-[hsl(var(--text-secondary))] whitespace-pre-wrap">{selectedIdea.description}</p>
                </div>

                {/* Engagement Bar */}
                <div className="flex items-center gap-4 px-5 py-3 border-b border-[hsl(var(--border))] text-sm">
                  <button onClick={() => handleUpvote(selectedIdea.id)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all ${(selectedIdea.upvotes || []).includes(currentUserId) ? 'bg-amber-500/10 text-amber-400 font-semibold' : 'text-[hsl(var(--text-muted))] hover:bg-[hsl(var(--surface))]'}`}>
                    <ThumbsUp size={16} fill={(selectedIdea.upvotes || []).includes(currentUserId) ? 'currentColor' : 'none'} />
                    {(selectedIdea.upvotes || []).length} {(selectedIdea.upvotes || []).length === 1 ? 'vote' : 'votes'}
                  </button>
                  <span className="flex items-center gap-2 text-[hsl(var(--text-muted))]"><MessageCircle size={16} /> {(selectedIdea.comments || []).length} comments</span>
                </div>

                {/* Comments - Social Media Style */}
                <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4 min-h-[100px] max-h-[300px]">
                  {(selectedIdea.comments || []).length === 0 ? (
                    <p className="text-sm text-[hsl(var(--text-muted))] text-center py-6 italic">No comments yet. Be the first!</p>
                  ) : (selectedIdea.comments || []).map((c) => (
                    <div key={c.id} className="flex gap-3">
                      {c.user_avatar ? (
                        <img src={c.user_avatar} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{c.user_name?.charAt(0)}</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="bg-[hsl(var(--surface))] rounded-2xl rounded-tl-sm px-4 py-2.5">
                          <p className="font-semibold text-xs mb-0.5">{c.user_name}</p>
                          <p className="text-sm text-[hsl(var(--text-secondary))]">{c.content}</p>
                        </div>
                        <p className="text-[10px] text-[hsl(var(--text-muted))] mt-1 pl-2">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Comment Input */}
                <div className="p-4 border-t border-[hsl(var(--border))] flex gap-2 items-center">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[hsl(var(--primary))] to-[hsl(var(--brand-orange))] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{currentUser.name?.charAt(0) || '?'}</div>
                  <MentionInput
                    value={newComment}
                    onChange={setNewComment}
                    onSubmit={handleAddComment}
                    placeholder="Write a comment... (type @ to mention)"
                  />
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </>
    );

  if (embedded) return content;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        {content}
      </div>
    </ResponsiveLayout>
  );
}
